import type { HTMLAttributes, ReactNode } from "react";

import type { TIconKey } from "@/ui/components/primitives/Icon";

/*-------------------------------------
|  Constants
-------------------------------------*/

export const STEPPER_TYPES = [ "pending", "order", "preparing", "delivery" ] as const;

export const STEPPER_STATES = [ "complete", "current", "disabled" ] as const;

export const STEPPER_TYPE_ICON_MAP = {
  order: "box",
  preparing: "chefHat",
  delivery: "truck",
  pending: "pending",
} as const satisfies Record<(typeof STEPPER_TYPES)[number], TIconKey>;

export const STEPPER_LIST_CLASS = "flex w-full items-start";

// Each item owns the interval after its badge. The final item only owns its
// badge, placing the first and last badges at the list's outer edges.
export const STEPPER_ITEM_CLASS = "relative flex min-w-0 flex-1 flex-col items-start gap-2 last:w-10 last:flex-none";

export const STEPPER_BADGE_BASE_CLASS = "relative z-10 flex size-10 shrink-0 items-center justify-center rounded-full border-2";

export const STEPPER_ICON_CLASS = "size-6";

export const STEPPER_BADGE_STATE_CLASS_MAP = {
  complete: "border-transparent bg-surface-inverse text-foreground-inverse",
  current: "border-strong bg-surface-default text-foreground",
  disabled: "border-strong bg-surface-default text-foreground opacity-50",
} as const;

// Starts 6px after this badge and ends 6px before the next badge.
export const STEPPER_CONNECTOR_BASE_CLASS = "absolute left-[46px] top-5 h-2 w-[calc(100%_-_52px)] -translate-y-1/2 rounded-full";

export const STEPPER_CONNECTOR_STATE_CLASS_MAP = {
  complete: "bg-inverse",
  incomplete: "bg-surface-elevated",
} as const;

export const STEPPER_LABEL_BASE_CLASS = "relative left-5 -translate-x-1/2 whitespace-wrap max-w-16";

export const STEPPER_LABEL_DISABLED_CLASS = "opacity-50";

/*-------------------------------------
|  Types
-------------------------------------*/

export type TStepperType = (typeof STEPPER_TYPES)[number];

export type TStepperState = (typeof STEPPER_STATES)[number];

export type TStepperConnectorState = keyof typeof STEPPER_CONNECTOR_STATE_CLASS_MAP;

export type TStepperStep = {
  id: string;
  type: TStepperType;
  label: ReactNode;
  state: TStepperState;
};

export type TStepperProps = {
  steps: TStepperStep[];
} & HTMLAttributes<HTMLOListElement>;

export type TStepperItemProps = Omit<TStepperStep, "id"> & {
  connector?: TStepperConnectorState;
} & HTMLAttributes<HTMLLIElement>;
